import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Zap, Crown, Infinity, ArrowLeft } from 'lucide-react';
import './PricingPage.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PricingPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const handleRedeemVoucher = async () => {
    if (!currentUser) {
      toast.error('Please log in to redeem a voucher');
      navigate('/login?redirect=/pricing');
      return;
    }

    if (!voucherCode.trim()) {
      toast.error('Please enter a voucher code');
      return;
    }

    setVoucherLoading(true);
    try {
      const response = await axios.post(
        `${API}/voucher/redeem?code=${voucherCode.trim()}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('user_session_token')}`
          }
        }
      );

      toast.success(`🎉 ${response.data.message}`);
      toast.success(`You've unlocked ${response.data.debates_limit} debates until ${response.data.valid_until}`);
      setVoucherCode('');
      
      // Reload user data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      toast.error(error.response?.data?.detail || 'Error redeeming voucher');
    } finally {
      setVoucherLoading(false);
    }
  };

  const handleUpgrade = async (tier, provider) => {
    if (!currentUser) {
      toast.error('Please log in to upgrade');
      navigate('/login?redirect=/pricing');
      return;
    }

    setLoading(true);
    try {
      // Create checkout session
      const response = await axios.post(`${API}/payment/create-checkout`, {
        tier: tier,
        provider: provider
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('user_session_token')}`
        }
      });

      // Redirect to checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Checkout URL not available');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Error creating checkout');
    } finally {
      setLoading(false);
    }
  };

  const pricingTiers = [
    {
      id: 'guest',
      name: 'Guest',
      icon: Zap,
      price: 'Free',
      description: 'Try Debatefy without registration',
      features: [
        '1 mini-debate per day',
        '3 arguments per debate',
        'AI opponent available',
        'Basic fact-checking'
      ],
      limitations: [
        'No archive function',
        'No statistics'
      ],
      cta: 'Start as guest',
      ctaAction: () => navigate('/choose-mode'),
      highlight: false,
      color: 'gray'
    },
    {
      id: 'free',
      name: 'Friend',
      icon: CheckCircle,
      price: 'Free',
      description: 'Register for more features',
      features: [
        '3 full debates per month',
        'Unlimited arguments',
        'AI & human opponents',
        'Full fact-checking',
        'Debate archive',
        'Personal statistics'
      ],
      limitations: [],
      cta: currentUser ? 'Current plan' : 'Register now',
      ctaAction: () => {
        if (!currentUser) {
          navigate('/register');
        }
      },
      highlight: false,
      color: 'blue',
      current: currentUser && currentUser.tier === 'free'
    },
    {
      id: 'starter',
      name: 'Starter',
      icon: Crown,
      price: '€4.99',
      priceDetail: 'per month',
      description: 'Perfect for regular debaters',
      features: [
        '15 debates per month',
        'Unlimited arguments',
        'Full fact-checking',
        'Advanced analytics',
        'Export function',
        'Personal statistics'
      ],
      limitations: [],
      cta: 'Upgrade to Starter',
      ctaAction: null, // Will be set below
      highlight: false,
      color: 'purple',
      current: currentUser && currentUser.tier === 'starter'
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: Zap,
      price: '€9.99',
      priceDetail: 'per month',
      description: 'For serious debaters',
      features: [
        '50 debates per month',
        'Unlimited arguments',
        'Full fact-checking',
        'Advanced analytics',
        'Pause Debate function',
        'Export function',
        'Personal statistics'
      ],
      limitations: [],
      cta: 'Upgrade to Pro',
      ctaAction: null,
      highlight: true,
      color: 'gradient',
      current: currentUser && currentUser.tier === 'pro'
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      icon: Infinity,
      price: '€19.99',
      priceDetail: 'per month',
      description: 'For professional debaters',
      features: [
        'Unlimited debates per month',
        'Unlimited arguments',
        'Full fact-checking',
        'Advanced analytics',
        'Pause Debate function',
        'Export function',
        'Personal statistics'
      ],
      limitations: [],
      cta: 'Upgrade to Unlimited',
      ctaAction: null,
      highlight: false,
      color: 'gold',
      current: currentUser && currentUser.tier === 'unlimited'
    }
  ];

  return (
    <div className="pricing-container">
      <div className="pricing-content">
        <Button 
          variant="ghost" 
          className="pricing-back-button"
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={20} /> Back
        </Button>

        <div className="pricing-header">
          <h1>Choose Your Plan</h1>
          <p>Flexible pricing for every need. Cancel anytime.</p>
        </div>

        {/* Voucher Redemption Section */}
        {currentUser && (
          <Card className="voucher-redemption-card">
            <CardHeader>
              <CardTitle>Have a Voucher Code?</CardTitle>
              <CardDescription>Redeem your voucher and get free debates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="voucher-redemption-form">
                <input
                  type="text"
                  placeholder="XXXX-XXXX-XXXX"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="voucher-input"
                  disabled={voucherLoading}
                />
                <Button
                  onClick={handleRedeemVoucher}
                  disabled={voucherLoading || !voucherCode.trim()}
                  className="voucher-redeem-btn"
                >
                  {voucherLoading ? 'Checking...' : 'Redeem'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="pricing-grid">
          {pricingTiers.map((tier) => {
            const Icon = tier.icon;
            const isCurrent = tier.current;
            const isDisabled = isCurrent || (tier.id === 'guest' || tier.id === 'free');

            return (
              <Card 
                key={tier.id} 
                className={`pricing-card ${tier.highlight ? 'pricing-card-highlight' : ''} ${isCurrent ? 'pricing-card-current' : ''}`}
              >
                {tier.highlight && (
                  <Badge className="pricing-badge-popular">Popular</Badge>
                )}
                {isCurrent && (
                  <Badge className="pricing-badge-current">Current</Badge>
                )}
                
                <CardHeader>
                  <div className="pricing-icon-wrapper">
                    <Icon className={`pricing-icon pricing-icon-${tier.color}`} />
                  </div>
                  <CardTitle className="pricing-tier-name">{tier.name}</CardTitle>
                  <CardDescription>{tier.description}</CardDescription>
                  
                  <div className="pricing-price-section">
                    <span className="pricing-price">{tier.price}</span>
                    {tier.priceDetail && (
                      <span className="pricing-price-detail">{tier.priceDetail}</span>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="pricing-features-list">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="pricing-feature-item">
                        <CheckCircle size={16} className="pricing-feature-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {tier.limitations.length > 0 && (
                    <ul className="pricing-limitations-list">
                      {tier.limitations.map((limitation, index) => (
                        <li key={index} className="pricing-limitation-item">
                          <span>• {limitation}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* CTA Buttons */}
                  {tier.ctaAction ? (
                    <Button 
                      className={`pricing-cta-button ${isCurrent ? 'pricing-cta-disabled' : ''}`}
                      onClick={tier.ctaAction}
                      disabled={isCurrent}
                    >
                      {tier.cta}
                    </Button>
                  ) : (
                    <div className="pricing-payment-buttons">
                      <Button 
                        className="pricing-cta-button pricing-stripe-button"
                        onClick={() => handleUpgrade(tier.id, 'stripe')}
                        disabled={loading || isCurrent}
                      >
                        {loading ? 'Loading...' : `Stripe: ${tier.cta}`}
                      </Button>
                      <Button 
                        className="pricing-cta-button pricing-paypal-button"
                        onClick={() => handleUpgrade(tier.id, 'paypal')}
                        disabled={loading || isCurrent}
                      >
                        {loading ? 'Loading...' : `PayPal: ${tier.cta}`}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="pricing-footer">
          <p>
            For questions contact us at <a href="mailto:info@debatefy.online">info@debatefy.online</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
